/**
 * Opportunity List View Queries
 *
 * React Query hooks for displaying opportunity data in list views.
 * Uses centralized mock data with localStorage persistence.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { persistedOpportunityListViews, persistedOpportunityRecords } from '../mockData/opportunities';
import { getRecordsForObject, getRecordsForObjectPage } from '../recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { USE_RDS_FOR_CORE_OBJECTS, isRdsCoreObject } from '../../config/rdsConfig';
import { normalizeRecordForListView } from '../../utils/listViewRecordNormalizer';
import type { ListView, ListViewRecord, ListViewData } from '../../types/listView';

/**
 * PE-1459: Stage to Probability mapping for Regal's sales process
 * Used to migrate old stage values to new Regal stages
 */
const STAGE_PROBABILITY_MAP: Record<string, number> = {
  'Meeting Set': 10,
  'Discovery': 20,
  'Pitch': 40,
  'Evaluation': 60,
  'Pending Deployment': 80,
  'Closed Won': 100,
  'Closed Lost': 0,
};

const VALID_STAGES = Object.keys(STAGE_PROBABILITY_MAP);

/**
 * PE-1459: Map old/legacy stage values to Regal's current stages
 */
const LEGACY_STAGE_MAP: Record<string, string> = {
  // Salesforce standard stages
  'Prospecting': 'Meeting Set',
  'Qualification': 'Discovery',
  'Needs Analysis': 'Discovery',
  'Value Proposition': 'Pitch',
  'Id. Decision Makers': 'Pitch',
  'Perception Analysis': 'Evaluation',
  'Proposal/Price Quote': 'Evaluation',
  'Negotiation/Review': 'Pending Deployment',
  // Common variations
  'prospecting': 'Meeting Set',
  'qualification': 'Discovery',
  'proposal': 'Evaluation',
  'negotiation': 'Pending Deployment',
  'closed won': 'Closed Won',
  'closed lost': 'Closed Lost',
  'Closed-Won': 'Closed Won',
  'Closed-Lost': 'Closed Lost',
};

/**
 * PE-1459: Migrate opportunity record to use Regal's stage values and probabilities
 */
function migrateOpportunityStage(record: ListViewRecord): ListViewRecord {
  const currentStage = (record as any).Stage__c as string;

  // If already a valid Regal stage, just ensure probability matches
  if (VALID_STAGES.includes(currentStage)) {
    const expectedProbability = STAGE_PROBABILITY_MAP[currentStage];
    // Only update probability if it's way off (allow some manual variation)
    return record;
  }

  // Try to map legacy stage to Regal stage
  const mappedStage = LEGACY_STAGE_MAP[currentStage] || LEGACY_STAGE_MAP[currentStage?.toLowerCase()] || 'Meeting Set';
  const mappedProbability = STAGE_PROBABILITY_MAP[mappedStage];

  console.log(`[PE-1459] Migrating opportunity ${record.id}: Stage "${currentStage}" → "${mappedStage}", Probability → ${mappedProbability}%`);

  return {
    ...record,
    Stage__c: mappedStage,
    Probability__c: mappedProbability,
  };
}

// Mock list views for Opportunities (using __c field naming convention)
const mockOpportunityListViews: ListView[] = [
  {
    id: 'lv_opps_all',
    name: 'All Opportunities',
    apiName: 'all_opportunities',
    objectApiName: 'Opportunity',
    description: 'All opportunity records',
    isDefault: true,
    columns: [
      { fieldApiName: 'Name__c', fieldLabel: 'Opportunity Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'ClientName__c', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'Amount__c', fieldLabel: 'Amount', fieldType: 'number', isVisible: true, order: 2 },
      { fieldApiName: 'Stage__c', fieldLabel: 'Stage', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'Probability__c', fieldLabel: 'Probability', fieldType: 'number', isVisible: true, order: 4 },
      { fieldApiName: 'CloseDate__c', fieldLabel: 'Close Date', fieldType: 'date', isVisible: true, order: 5 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 6 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_opps_open',
    name: 'Open Opportunities',
    apiName: 'open_opportunities',
    objectApiName: 'Opportunity',
    description: 'Active opportunities in pipeline',
    isDefault: false,
    columns: [
      { fieldApiName: 'Name__c', fieldLabel: 'Opportunity Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'ClientName__c', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'Amount__c', fieldLabel: 'Amount', fieldType: 'number', isVisible: true, order: 2 },
      { fieldApiName: 'Stage__c', fieldLabel: 'Stage', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'Probability__c', fieldLabel: 'Probability', fieldType: 'number', isVisible: true, order: 4 },
      { fieldApiName: 'CloseDate__c', fieldLabel: 'Close Date', fieldType: 'date', isVisible: true, order: 5 },
      { fieldApiName: 'NextStep__c', fieldLabel: 'Next Step', fieldType: 'text', isVisible: true, order: 6 },
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_opps_q1',
    name: 'Closing This Quarter',
    apiName: 'closing_this_quarter',
    objectApiName: 'Opportunity',
    description: 'Opportunities closing in Q1 2025',
    isDefault: false,
    columns: [
      { fieldApiName: 'Name__c', fieldLabel: 'Opportunity Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'ClientName__c', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'Amount__c', fieldLabel: 'Amount', fieldType: 'number', isVisible: true, order: 2 },
      { fieldApiName: 'Stage__c', fieldLabel: 'Stage', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'CloseDate__c', fieldLabel: 'Close Date', fieldType: 'date', isVisible: true, order: 4 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    sortBy: {
      fieldApiName: 'CloseDate__c',
      direction: 'asc'
    },
    createdBy: 'Admin',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_opps_won',
    name: 'Closed Won',
    apiName: 'closed_won_opportunities',
    objectApiName: 'Opportunity',
    description: 'Successfully closed opportunities',
    isDefault: false,
    columns: [
      { fieldApiName: 'Name__c', fieldLabel: 'Opportunity Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'ClientName__c', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'Amount__c', fieldLabel: 'Amount', fieldType: 'number', isVisible: true, order: 2 },
      { fieldApiName: 'CloseDate__c', fieldLabel: 'Close Date', fieldType: 'date', isVisible: true, order: 3 },
      { fieldApiName: 'Type__c', fieldLabel: 'Type', fieldType: 'text', isVisible: true, order: 4 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'Stage__c', operator: 'equals', value: 'Closed Won' }
    ],
    sortBy: {
      fieldApiName: 'CloseDate__c',
      direction: 'desc'
    },
    createdBy: 'Admin',
    createdDate: '2024-04-01T10:00:00Z'
  }
];

// Mock opportunity records (using __c field naming convention)
const mockOpportunityRecords: ListViewRecord[] = [
  {
    id: 'opp_001',
    Name__c: 'Enterprise Software License - Tech Innovations',
    ClientId__c: 'client_001',
    ClientName__c: 'Tech Innovations Inc',
    Amount__c: 250000,
    Stage__c: 'Evaluation',
    Probability__c: 80,
    CloseDate__c: '2025-02-28',
    Owner__c: 'John Smith',
    Type__c: 'New Business',
    LeadSource__c: 'Website',
    NextStep__c: 'Send final contract for review',
    CreatedDate__c: '2024-12-15T09:00:00Z',
    LastModifiedDate__c: '2025-01-15T14:30:00Z'
  },
  {
    id: 'opp_002',
    Name__c: 'Cloud Migration Services - Global Manufacturing',
    ClientId__c: 'client_002',
    ClientName__c: 'Global Manufacturing Corp',
    Amount__c: 500000,
    Stage__c: 'Pitch',
    Probability__c: 60,
    CloseDate__c: '2025-03-15',
    Owner__c: 'Sarah Johnson',
    Type__c: 'New Business',
    LeadSource__c: 'Referral',
    NextStep__c: 'Deliver technical proposal presentation',
    CreatedDate__c: '2025-01-05T10:30:00Z',
    LastModifiedDate__c: '2025-01-14T11:00:00Z'
  },
  {
    id: 'opp_003',
    Name__c: 'POS System Upgrade - Retail Solutions',
    ClientId__c: 'client_003',
    ClientName__c: 'Retail Solutions LLC',
    Amount__c: 150000,
    Stage__c: 'Meeting Set',
    Probability__c: 20,
    CloseDate__c: '2025-04-30',
    Owner__c: 'Michael Chen',
    Type__c: 'Existing Customer - Upgrade',
    LeadSource__c: 'Trade Show',
    NextStep__c: 'Schedule discovery call',
    CreatedDate__c: '2025-01-10T14:00:00Z',
    LastModifiedDate__c: '2025-01-16T09:15:00Z'
  },
  {
    id: 'opp_004',
    Name__c: 'Financial Analytics Platform - FSG',
    ClientId__c: 'client_004',
    ClientName__c: 'Financial Services Group',
    Amount__c: 750000,
    Stage__c: 'Discovery',
    Probability__c: 40,
    CloseDate__c: '2025-03-31',
    Owner__c: 'Emily Davis',
    Type__c: 'New Business',
    LeadSource__c: 'Cold Call',
    NextStep__c: 'Complete needs assessment',
    CreatedDate__c: '2024-12-20T09:15:00Z',
    LastModifiedDate__c: '2025-01-13T16:45:00Z'
  },
  {
    id: 'opp_005',
    Name__c: 'Healthcare Data Security Solution',
    ClientId__c: 'client_005',
    ClientName__c: 'Healthcare Innovations',
    Amount__c: 350000,
    Stage__c: 'Closed Won',
    Probability__c: 100,
    CloseDate__c: '2025-01-15',
    Owner__c: 'David Wilson',
    Type__c: 'New Business',
    LeadSource__c: 'Partner',
    NextStep__c: 'Begin implementation',
    CreatedDate__c: '2024-11-10T11:30:00Z',
    LastModifiedDate__c: '2025-01-15T10:15:00Z'
  },
  {
    id: 'opp_006',
    Name__c: 'Project Management Software - Construction Pro',
    ClientId__c: 'client_006',
    ClientName__c: 'Construction Pro LLC',
    Amount__c: 85000,
    Stage__c: 'Closed Lost',
    Probability__c: 0,
    CloseDate__c: '2025-01-10',
    Owner__c: 'Sarah Johnson',
    Type__c: 'New Business',
    LeadSource__c: 'Website',
    NextStep__c: 'Archive opportunity',
    CreatedDate__c: '2024-11-05T13:00:00Z',
    LastModifiedDate__c: '2025-01-10T09:30:00Z'
  },
  {
    id: 'opp_007',
    Name__c: 'Learning Management System - EdTech',
    ClientId__c: 'client_007',
    ClientName__c: 'Education Tech Solutions',
    Amount__c: 200000,
    Stage__c: 'Evaluation',
    Probability__c: 75,
    CloseDate__c: '2025-02-15',
    Owner__c: 'John Smith',
    Type__c: 'New Business',
    LeadSource__c: 'Referral',
    NextStep__c: 'Negotiate pricing and terms',
    CreatedDate__c: '2024-12-01T15:45:00Z',
    LastModifiedDate__c: '2025-01-14T11:20:00Z'
  },
  {
    id: 'opp_008',
    Name__c: 'Fleet Management System - Logistics',
    ClientId__c: 'client_008',
    ClientName__c: 'Logistics Central',
    Amount__c: 425000,
    Stage__c: 'Pitch',
    Probability__c: 50,
    CloseDate__c: '2025-03-20',
    Owner__c: 'Michael Chen',
    Type__c: 'Existing Customer - Replacement',
    LeadSource__c: 'Trade Show',
    NextStep__c: 'Submit detailed proposal',
    CreatedDate__c: '2024-12-28T08:30:00Z',
    LastModifiedDate__c: '2025-01-15T14:00:00Z'
  },
  {
    id: 'opp_009',
    Name__c: 'Marketing Automation Platform Expansion',
    ClientId__c: 'client_009',
    ClientName__c: 'Marketing Pros',
    Amount__c: 125000,
    Stage__c: 'Discovery',
    Probability__c: 35,
    CloseDate__c: '2025-04-15',
    Owner__c: 'Emily Davis',
    Type__c: 'Existing Customer - Upgrade',
    LeadSource__c: 'Website',
    NextStep__c: 'Demo advanced features',
    CreatedDate__c: '2025-01-08T09:00:00Z',
    LastModifiedDate__c: '2025-01-16T10:30:00Z'
  },
  {
    id: 'opp_010',
    Name__c: 'Enterprise DevOps Tools - Software Dynamics',
    ClientId__c: 'client_010',
    ClientName__c: 'Software Dynamics',
    Amount__c: 600000,
    Stage__c: 'Closed Won',
    Probability__c: 100,
    CloseDate__c: '2025-01-20',
    Owner__c: 'David Wilson',
    Type__c: 'New Business',
    LeadSource__c: 'Referral',
    NextStep__c: 'Kickoff meeting scheduled',
    CreatedDate__c: '2024-11-15T11:00:00Z',
    LastModifiedDate__c: '2025-01-20T10:00:00Z'
  }
];

/**
 * Get available list views for opportunities
 */
export function useOpportunityListViews() {
  return useQuery({
    queryKey: ['opportunityListViews'],
    queryFn: async () => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      return persistedOpportunityListViews.get();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

const DEFAULT_PAGE_SIZE = 25;

/**
 * Get list view data (view configuration + records) with server-side pagination
 */
export function useOpportunityListViewData(
  listViewId: string,
  options?: { page?: number; pageSize?: number }
) {
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const useBackend = shouldUseGobodo() || (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject('Opportunity'));

  return useQuery<ListViewData>({
    queryKey: ['opportunityListViewData', listViewId, page, pageSize],
    queryFn: async () => {
      const allViews = persistedOpportunityListViews.get();
      const listView = allViews.find(lv => lv.id === listViewId);
      if (!listView) throw new Error('List view not found');

      if (useBackend) {
        const [pageResult, clients] = await Promise.all([
          getRecordsForObjectPage('Opportunity', { page, size: pageSize }),
          getRecordsForObject('Client__c')
        ]);
        let records: ListViewRecord[] = pageResult.records.map(r => normalizeRecordForListView('Opportunity', r) as ListViewRecord);
        records = records.map(record => {
          const clientId = (record as any).ClientId__c;
          if (clientId && !(record as any).ClientName__c) {
            const client = clients.find((c: any) => c.id === clientId);
            if (client) {
              return { ...record, ClientName__c: (client.name ?? client.label ?? (client as any).Name__c) as string };
            }
          }
          return record;
        });

        if (listView.filters?.length) {
          listView.filters.forEach(filter => {
            records = records.filter(record => {
              const fieldValue = record[filter.fieldApiName];
              switch (filter.operator) {
                case 'equals': return fieldValue === filter.value;
                case 'notEquals': return fieldValue !== filter.value;
                case 'contains': return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
                case 'startsWith': return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
                default: return true;
              }
            });
          });
        }
        if (listView.id === 'lv_opps_open') {
          records = records.filter(record =>
            (record as any).Stage__c !== 'Closed Won' && (record as any).Stage__c !== 'Closed Lost'
          );
        }
        if (listView.id === 'lv_opps_q1') {
          records = records.filter(record => {
            const closeDate = new Date((record as any).CloseDate__c as string);
            return closeDate >= new Date('2025-01-01') && closeDate <= new Date('2025-03-31');
          });
        }
        if (listView.sortBy) {
          records = [...records].sort((a, b) => {
            const aValue = a[listView.sortBy!.fieldApiName];
            const bValue = b[listView.sortBy!.fieldApiName];
            if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
            return aValue < bValue ? 1 : -1;
          });
        }
        return { listView, records, totalRecords: pageResult.totalRecords };
      }

      // PE-1459: Migrate any records with legacy stage values to Regal's stages
      const allRecords = persistedOpportunityRecords.get().map(migrateOpportunityStage);
      let filtered: ListViewRecord[] = [...allRecords];
      if (listView.filters?.length) {
        listView.filters.forEach(filter => {
          filtered = filtered.filter(record => {
            const fieldValue = record[filter.fieldApiName];
            switch (filter.operator) {
              case 'equals': return fieldValue === filter.value;
              case 'notEquals': return fieldValue !== filter.value;
              case 'contains': return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
              case 'startsWith': return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
              default: return true;
            }
          });
        });
      }
      if (listView.id === 'lv_opps_open') {
        filtered = filtered.filter(record =>
          (record as any).Stage__c !== 'Closed Won' && (record as any).Stage__c !== 'Closed Lost'
        );
      }
      if (listView.id === 'lv_opps_q1') {
        filtered = filtered.filter(record => {
          const closeDate = new Date((record as any).CloseDate__c as string);
          return closeDate >= new Date('2025-01-01') && closeDate <= new Date('2025-03-31');
        });
      }
      if (listView.sortBy) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = a[listView.sortBy!.fieldApiName];
          const bValue = b[listView.sortBy!.fieldApiName];
          if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
          return aValue < bValue ? 1 : -1;
        });
      }
      const totalRecords = filtered.length;
      const start = page * pageSize;
      const records = filtered.slice(start, start + pageSize);
      return { listView, records, totalRecords };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!listViewId,
    placeholderData: keepPreviousData,
  });
}

/**
 * Get default list view for opportunities
 */
export function useDefaultOpportunityListView() {
  return useQuery({
    queryKey: ['defaultOpportunityListView'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));

      const allViews = persistedOpportunityListViews.get();
      const defaultView = allViews.find(lv => lv.isDefault);

      return defaultView || allViews[0];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Update an opportunity list view
 * Used by EditViewModal to persist changes
 */
export function useUpdateOpportunityListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ListView> }) => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update in localStorage via mockDataManager
      const updated = persistedOpportunityListViews.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) {
        throw new Error('List view not found');
      }

      return updated;
    },
    onSuccess: (data) => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['opportunityListViews'] });
      queryClient.invalidateQueries({ queryKey: ['opportunityListViewData', data.id] });
      queryClient.invalidateQueries({ queryKey: ['defaultOpportunityListView'] });
    },
  });
}
